/**
 * B01 3D configurator analytics — a thin, dependency-free wrapper around the
 * dataLayer that the Stape Conversion Tracking app block already puts on
 * every page.
 *
 * Note that the Stape block only *creates* that array (and bridges Shopify's
 * own events into it from the web-pixel sandbox). As of 2026-09-23 no web GTM
 * container is loaded on this storefront, so nothing reads the dataLayer and
 * these events reach nobody. Closing that chain is configuration, not code —
 * see the Prerequisites section of docs/b01-analytics.md.
 *
 * Deliberately kept out of product-3d-viewer.js: that file is already 565
 * lines of viewer/gallery/AR orchestration, and instrumentation is a separate
 * concern with a separate lifetime. It should be readable, auditable and
 * removable on its own, and the viewer should only ever call B01Track(...).
 *
 * Privacy: every event here is an anonymous counter or a duration in
 * milliseconds. No identifiers, no cookies, no localStorage, nothing derived
 * from the visitor beyond a coarse ios/android/desktop split. A dataLayer
 * push is not itself a storage access, so this runs regardless of consent
 * state; whether a push becomes a GA4 hit is decided downstream by Consent
 * Mode v2 (Consentmo). The consequence for whoever reads the reports:
 * visitors who declined analytics never reach GA4, so every count derived
 * from these events is a lower bound, not a complete picture.
 */
(function () {
  'use strict';

  var CHAIR = 'B01';

  // Coarse enough to be a reporting dimension, never a fingerprint. Mirrors
  // the same UA checks product-3d-viewer.js makes to pick an AR path, so the
  // two can't disagree about what kind of device this is.
  function deviceClass() {
    var ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'desktop';
  }

  var device = deviceClass();

  // Facts the viewer knows but this module can't work out for itself (the
  // low-tier device verdict, the WebXR probe result). Registered once and
  // merged into every subsequent event, so call sites stay one-liners.
  var context = {};

  // Guard for events that must fire at most once per 3D session — the
  // first-interaction event above all, which would otherwise fire on every
  // drag. Cleared by resetSession() each time the 3D pane opens.
  var fired = {};

  function own(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  function track(name, params) {
    if (!name) return;
    var dl = (window.dataLayer = window.dataLayer || []);
    var payload = { event: name, chair: CHAIR, device: device };
    var key;
    for (key in context) if (own(context, key)) payload[key] = context[key];
    for (key in params) if (own(params, key)) payload[key] = params[key];
    try {
      dl.push(payload);
    } catch (e) {
      // A reporting failure must never take the configurator down with it.
      if (window.console && console.debug) console.debug('[b01] track failed', e);
    }
  }

  // Fires `name` at most once per 3D session. Returns whether it actually
  // fired, so a caller can tell a first interaction from a later one without
  // keeping its own flag.
  track.once = function (name, params) {
    if (fired[name]) return false;
    fired[name] = true;
    track(name, params);
    return true;
  };

  track.resetSession = function () {
    fired = {};
  };

  track.setContext = function (next) {
    for (var key in next) if (own(next, key)) context[key] = next[key];
  };

  window.B01Track = track;
})();
